import type { PopClipMetadata } from '@ps/models/lib/popclip';

/**
 * Resolve a potentially-relative URL to an absolute one by prepending cdnUrl.
 * Matches RN's `processResponseData` → `makeUrl` pattern from clipsApis.ts.
 */
function makeAbsoluteUrl(
  url: string | undefined,
  cdnUrl: string | undefined,
): string | undefined {
  if (!url) return undefined;
  if (url.startsWith('https://')) return url;
  return cdnUrl ? `${cdnUrl}${url}` : undefined;
}

/**
 * Whether a resolved thumbnail URL points at a VIDEO file rather than an image.
 * Both `thumbnailURL` and the `videoCoverUrl` cover can be an `.mp4` cover clip
 * (not an image), so consumers must render a video-URL thumbnail in a `<video>`
 * element (first frame), NOT an `<img>` — 1:1 with RN, which renders
 * `thumbnailURL` via `PopImage` but `videoCoverUrl` via the `VideoCover`
 * `<Video>` component (`SeriesElementListItem`). Use {@link VideoPoster}.
 */
export function isVideoThumbnailUrl(url: string): boolean {
  return /\.(mp4|mov|m4v|webm)(\?.*)?$/i.test(url);
}

export function resolvePopClipThumbnailUrl(
  metaData: PopClipMetadata | undefined,
  cdnUrl: string | undefined,
): string | null {
  if (!metaData) return null;

  const customThumbnail = makeAbsoluteUrl(
    metaData.customThumbnailImageURL,
    cdnUrl,
  );
  if (customThumbnail) return customThumbnail;

  if (metaData.defaultThumbnailImageStatus === 'processed') {
    const defaultThumbnail = makeAbsoluteUrl(
      metaData.defaultThumbnailImageURL,
      cdnUrl,
    );
    if (defaultThumbnail) return defaultThumbnail;
  }

  // `thumbnailURL` may be an image OR a video cover (`.mp4`). Return it either
  // way — cells render a video URL in a `<video>` (first frame) and an image in
  // an `<img>` (see {@link isVideoThumbnailUrl} / `VideoPoster`). Previously this
  // rejected non-image `thumbnailURL`s to `null`, so a clip whose only thumbnail
  // is a video cover fell through to a grey placeholder.
  const thumbnail = makeAbsoluteUrl(metaData.thumbnailURL, cdnUrl);
  if (thumbnail) return thumbnail;

  // Final fallback: the video's cover / first frame. Prefer the purpose-built
  // `videoCoverUrl`, then the transcoded `processedURL`, then the raw
  // `videoURL`. Any of these may be an `.mp4`; cells render a video URL via
  // `VideoPoster` (`preload="metadata"` → first frame only, NOT the whole clip),
  // never an `<img>`. Falling all the way through to `videoURL` means a clip
  // with no image/cover still shows its first frame instead of a grey box.
  const cover =
    makeAbsoluteUrl(metaData.videoCoverUrl, cdnUrl) ??
    makeAbsoluteUrl(metaData.processedURL, cdnUrl) ??
    makeAbsoluteUrl(metaData.videoURL, cdnUrl);
  if (cover) return cover;

  return null;
}
